'use client';

import { Helmet } from 'react-helmet-async';
import { Link } from '@trashlab/sitesapp/src/organizations/redebins/components/shared/link';
import { OrderButtons } from '@trashlab/sitesapp/src/organizations/redebins/template/components/shared/order-buttons';
import { ProductCards } from '@trashlab/sitesapp/src/organizations/redebins/template/components/home/product-cards';
import { ReadyWhenYouAre } from '@trashlab/sitesapp/src/organizations/redebins/template/components/shared/ready-when-you-are';
import { ServiceAreasSection } from '@trashlab/sitesapp/src/organizations/redebins/template/components/shared/service-areas-section';

interface ServiceAreaSEO {
  title: string;
  description: string;
  keywords: string[];
}

interface ServiceAreaData {
  cityName: string;
  countyName: string;
  state: string;
  heroTitle: string;
  seo: ServiceAreaSEO;
  introParagraph: string;
  whyChooseReasons: string[];
  residentialUses: string[];
  residentialBenefits: string;
  commercialUses: string[];
  communityParagraph: string;
  alternativeResources: Array<{
    name: string;
    url: string;
    description: string;
  }>;
  howToRentSteps: string[];
  closingParagraph: string;
}

// Content data for each service area
const SERVICE_AREA_CONTENT: Record<string, ServiceAreaData> = {
  'dumpster-rental-minneapolis-mn': {
    cityName: 'Minneapolis',
    countyName: 'Hennepin',
    state: 'MN',
    heroTitle: 'Dumpster Rental in Minneapolis',
    seo: {
      title:
        'Dumpster Rental in Minneapolis, MN | Residential & Commercial | RED-E-BINS',
      description:
        'Reliable dumpster rental in Minneapolis, MN for residential and commercial projects. Multiple sizes available with on-time delivery from RED-E-BINS.',
      keywords: [
        'dumpster rental Minneapolis',
        'Minneapolis dumpster rental',
        'roll off dumpster Minneapolis',
        'residential dumpster Minneapolis',
        'commercial dumpster Minneapolis',
        'junk removal Minneapolis',
        'waste removal Minneapolis MN',
        '5 yard dumpster Minneapolis',
        '10 yard dumpster Minneapolis',
        'construction debris removal Minneapolis',
        'Hennepin County dumpster rental',
      ],
    },
    introParagraph:
      'Need a dumpster rental in Minneapolis? RED-E-BINS is your trusted service provider. We offer reliable and affordable dumpster rentals for homeowners and businesses. Whether you are decluttering your home or handling construction debris, we have the right solution for you.',
    whyChooseReasons: [
      'We are local, which means fast service and a commitment to the Minneapolis community.',
      'We focus on eco-friendly disposal by recycling and donating to reduce landfill waste.',
      'We provide clear pricing with no hidden fees. You only pay what you see.',
      'We think customer service matters.',
      'We provide ecommerce tools for easy checkout, communication, and tracking your dumpster rental.',
    ],
    residentialUses: [
      'Garage cleanouts: Remove old furniture, tools, and clutter effortlessly.',
      'Home renovations: Dispose of drywall, flooring, and cabinets efficiently.',
      'Yard waste: Clear branches, leaves, and landscaping debris with ease.',
    ],
    residentialBenefits:
      'Our services save homeowners time and effort. Additionally, we handle waste responsibly. This means you can focus on your project without worrying about disposal logistics. With our help, waste removal becomes stress-free and convenient.',
    commercialUses: [
      'Retail cleanouts: Dispose of shelves, packaging, and outdated items easily.',
      'Construction debris: Use a 10-yard dumpster for wood, concrete, and heavy materials.',
      'Office upgrades: Clear out desks, chairs, and electronics without hassle.',
    ],
    communityParagraph:
      "At RED-E-BINS, we are more than a roll-off company—we're neighbors. Whether you live near Nicollet Avenue or Hennepin Avenue, we deliver dumpsters on-time and on-demand. We understand your unique needs and tailor our solutions accordingly. Plus, our customer service focused team is always ready to assist, ensuring a hassle-free experience from start to finish.",
    alternativeResources: [
      {
        name: 'Hennepin County Recycling',
        url: 'https://www.hennepin.us/recycling',
        description: 'Drop off electronics and recyclables.',
      },
      {
        name: 'Bridging',
        url: 'https://www.bridging.org/',
        description: 'Donate gently used furniture and household items.',
      },
      {
        name: '2nd Chance Recycling',
        url: 'https://www.secondchancerecyclingmn.com/',
        description: 'Donate tools and building materials.',
      },
    ],
    howToRentSteps: [
      'Choose a size: Select between a 5-yard or 10-yard dumpster.',
      'Schedule delivery: We deliver to your Minneapolis location.',
      'Fill it up: Load the dumpster at your convenience.',
      'Request pickup: We take care of the disposal responsibly.',
    ],
    closingParagraph:
      'Renting a dumpster in Minneapolis is simple with RED-E-BINS. Contact us today to get started! Our team ensures a smooth and efficient process, providing you with satisfaction every step of the way.',
  },
  'dumpster-rental-st-paul-mn': {
    cityName: 'St. Paul',
    countyName: 'Ramsey',
    state: 'MN',
    heroTitle: 'Dumpster Rental in St. Paul',
    seo: {
      title:
        'Dumpster Rental in St. Paul, MN | Fast & Affordable | RED-E-BINS',
      description:
        'Fast, affordable dumpster rental in St. Paul, MN. Ideal for cleanouts, renovations, and construction debris. Book online with RED-E-BINS.',
      keywords: [
        'dumpster rental St. Paul',
        'St. Paul dumpster rental',
        'roll off dumpster St. Paul',
        'residential dumpster St. Paul',
        'commercial dumpster St. Paul',
        'junk removal St. Paul',
        'waste removal St. Paul MN',
        '5 yard dumpster St. Paul',
        '10 yard dumpster St. Paul',
        'construction debris removal St. Paul',
        'Ramsey County dumpster rental',
      ],
    },
    introParagraph:
      'Need a dumpster rental in St. Paul? RED-E-BINS offers fast and efficient dumpster rental services tailored to meet your residential or commercial needs in St. Paul. Whether you\'re cleaning up after a home renovation or managing large-scale construction debris, we provide a range of dumpster sizes to make waste disposal easy.',
    whyChooseReasons: [
      'We offer local expertise: As a local business, we know the St. Paul area and are committed to delivering quick and reliable service.',
      'Eco-friendly disposal: RED-E-BINS promotes recycling and green disposal practices to help minimize landfill waste.',
      'We provide no hidden fees: With our transparent pricing, you can trust that what you see is what you pay for your dumpster rental.',
    ],
    residentialUses: [
      'Declutter your home: Get rid of old furniture, appliances, and unwanted household items in St. Paul with our residential dumpsters.',
      'Renovation cleanup: Whether it\'s remodeling your kitchen or replacing old flooring, our dumpsters can handle construction debris from your St. Paul home.',
      'Yard waste disposal: Clear out leaves, tree branches, and garden waste after a landscaping project with a convenient dumpster rental in St. Paul.',
    ],
    residentialBenefits: '',
    commercialUses: [
      'Retail store cleanouts: Dispose of old displays, fixtures, and packaging waste easily with a commercial dumpster rental in St. Paul.',
      'Construction debris: Our dumpsters are perfect for removing wood, drywall, concrete, and other debris from construction sites in St. Paul.',
      'Office cleanouts: Whether you\'re moving or updating office furniture, our dumpsters will help you quickly clear out unwanted items in St. Paul.',
    ],
    communityParagraph:
      "At RED-E-BINS, we care about St. Paul. We're more than just a dumpster rental service; we are part of the community. Whether you're located in the heart of downtown St. Paul or in a suburban neighborhood, our team is ready to provide fast and reliable service.",
    alternativeResources: [
      {
        name: 'St. Paul Public Works Recycling',
        url: 'https://www.stpaul.gov/departments/public-works/recycling-and-waste-reduction/residential-recycling-program',
        description: 'A convenient drop-off site for recyclables and other waste materials.',
      },
      {
        name: 'ReStore Minneapolis',
        url: 'https://www.restoreminneapolis.org/',
        description: 'Donate gently used furniture, building materials, and more in St. Paul.',
      },
      {
        name: 'Hennepin County Hazardous Waste',
        url: 'https://www.hennepin.us/recycling-hazardous-waste',
        description: 'Properly dispose of hazardous materials like paint, electronics, and chemicals.',
      },
    ],
    howToRentSteps: [
      'Select your dumpster size: Choose between our 5-yard and 10-yard dumpsters based on your project needs.',
      'Schedule delivery: Let us know when and where you need the dumpster, and we\'ll handle the rest.',
      'Fill the dumpster: Take your time to load up your waste materials, and don\'t worry about any rush.',
      'Pickup and disposal: Once you\'re done, call us for quick pickup and responsible disposal of your waste in St. Paul.',
    ],
    closingParagraph: '',
  },
  'dumpster-rental-st-louis-park-mn': {
    cityName: 'St. Louis Park',
    countyName: 'Hennepin',
    state: 'MN',
    heroTitle: 'Dumpster Rental in St. Louis Park',
    seo: {
      title:
        'Dumpster Rental in St. Louis Park, MN | Affordable Bins | RED-E-BINS',
      description:
        'Rent affordable dumpsters in St. Louis Park, MN. Perfect for home cleanouts, remodels, and construction projects. Fast delivery from RED-E-BINS.',
      keywords: [
        'dumpster rental St. Louis Park',
        'St. Louis Park dumpster rental',
        'roll off dumpster St. Louis Park',
        'residential dumpster St. Louis Park',
        'commercial dumpster St. Louis Park',
        'junk removal St. Louis Park',
        'waste removal St. Louis Park MN',
        '5 yard dumpster St. Louis Park',
        '10 yard dumpster St. Louis Park',
        'construction debris removal St. Louis Park',
        'Hennepin County dumpster rental',
      ],
    },
    introParagraph:
      'Need a dumpster rental in St. Louis Park? RED-E-BINS is your trusted service provider. We offer reliable and affordable dumpster rentals for homeowners and businesses. Whether you are decluttering your home or handling construction debris, we have the right solution for you.',
    whyChooseReasons: [
      'We are local, which means fast service and a commitment to the St. Louis Park community.',
      'We focus on eco-friendly disposal by recycling and donating to reduce landfill waste.',
      'We provide clear pricing with no hidden fees. You only pay what you see.',
      'We think customer service matters.',
      'We provide ecommerce tools for easy checkout, communication, and tracking your dumpster rental.',
    ],
    residentialUses: [
      'Garage cleanouts: Remove old furniture, tools, and clutter effortlessly.',
      'Home renovations: Dispose of drywall, flooring, and cabinets efficiently.',
      'Yard waste: Clear branches, leaves, and landscaping debris with ease.',
    ],
    residentialBenefits:
      'Our services save homeowners time and effort. Additionally, we handle waste responsibly. This means you can focus on your project without worrying about disposal logistics. With our help, waste removal becomes stress-free and convenient.',
    commercialUses: [
      'Retail cleanouts: Dispose of shelves, packaging, and outdated items easily.',
      'Construction debris: Use a 10-yard dumpster for wood, concrete, and heavy materials.',
      'Office upgrades: Clear out desks, chairs, and electronics without hassle.',
    ],
    communityParagraph:
      "At RED-E-BINS, we are more than a roll-off company—we're neighbors. We deliver dumpsters on-time and on-demand throughout St. Louis Park. We understand your unique needs and tailor our solutions accordingly. Plus, our customer service focused team is always ready to assist, ensuring a hassle-free experience from start to finish.",
    alternativeResources: [
      {
        name: 'Hennepin County Recycling',
        url: 'https://www.hennepin.us/recycling',
        description: 'Drop off electronics and recyclables.',
      },
      {
        name: 'Bridging',
        url: 'https://www.bridging.org/',
        description: 'Donate gently used furniture and household items.',
      },
      {
        name: '2nd Chance Recycling',
        url: 'https://www.secondchancerecyclingmn.com/',
        description: 'Donate tools and building materials.',
      },
    ],
    howToRentSteps: [
      'Choose a size: Select between a 5-yard or 10-yard dumpster.',
      'Schedule delivery: We deliver to your St. Louis Park location.',
      'Fill it up: Load the dumpster at your convenience.',
      'Request pickup: We take care of the disposal responsibly.',
    ],
    closingParagraph:
      'Renting a dumpster in St. Louis Park is simple with RED-E-BINS. Contact us today to get started! Our team ensures a smooth and efficient process, providing you with satisfaction every step of the way.',
  },
};

interface ServiceAreaCityContentProps {
  citySlug: string;
}

export const ServiceAreaCityContent = ({
  citySlug,
}: ServiceAreaCityContentProps) => {
  const data = SERVICE_AREA_CONTENT[citySlug];

  if (!data) {
    return (
      <div className="py-16 text-center">
        <h1 className="text-2xl font-bold">Service Area Not Found</h1>
        <p className="mt-4">
          <Link
            href="/service-areas"
            className="text-[#E31837] hover:underline"
          >
            View all service areas
          </Link>
        </p>
      </div>
    );
  }

  // Build canonical URL (flattened structure for SEO)
  const canonicalUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/service-areas/${citySlug}`
      : `https://mn.redebins.com/service-areas/${citySlug}`;

  // LocalBusiness structured data for SEO
  // Note: Aggregate ratings removed - only use ratings from verified review platforms (Google, Yelp, etc.)
  // to avoid Google penalties for self-reported ratings
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': ['WasteManagementCompany', 'LocalBusiness'],
    '@id': canonicalUrl,
    name: 'RED-E-BINS Minnesota',
    additionalType: 'Dumpster rental service',
    description: data.seo.description,
    url: canonicalUrl,
    telephone: '+1-952-931-2577',
    email: 'customerservice@redebins.com',
    image: 'https://mn.redebins.com/redebins/images/dumpster-10yd.png',
    address: {
      '@type': 'PostalAddress',
      addressLocality: data.cityName,
      addressRegion: data.state,
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      // Business HQ coordinates (St. Louis Park)
      latitude: 44.95531996466011,
      longitude: -93.37124610263096,
    },
    areaServed: {
      '@type': 'City',
      name: data.cityName,
      containedInPlace: {
        '@type': 'AdministrativeArea',
        name: `${data.countyName} County, ${data.state}`,
      },
    },
    serviceType: [
      'Dumpster Rental',
      'Roll-Off Dumpster',
      'Waste Removal',
      'Junk Removal',
      'Construction Debris Removal',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Dumpster Rental Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '5-Yard Dumpster Rental',
            description:
              'Compact 5-yard dumpster perfect for small DIY projects and heavier materials',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: '10-Yard Dumpster Rental',
            description:
              'Medium-sized 10-yard dumpster excellent for medium clean-outs and demo projects',
          },
        },
      ],
    },
  };

  return (
    <>
      {/* Page-Specific SEO */}
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{data.seo.title}</title>
        <meta name="description" content={data.seo.description} />
        <meta name="keywords" content={data.seo.keywords.join(', ')} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={data.seo.title} />
        <meta property="og:description" content={data.seo.description} />
        <meta property="og:site_name" content="RED-E-BINS Minnesota" />
        <meta
          property="og:image"
          content="https://mn.redebins.com/redebins/images/dumpster-10yd.png"
        />
        <meta property="og:image:alt" content="RED-E-BINS Dumpster Rental" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={data.seo.title} />
        <meta name="twitter:description" content={data.seo.description} />
        <meta
          name="twitter:image"
          content="https://mn.redebins.com/redebins/images/dumpster-10yd.png"
        />

        {/* Robots */}
        <meta name="robots" content="index, follow, max-image-preview:large" />

        {/* Geographic */}
        <meta name="geo.region" content={`US-${data.state}`} />
        <meta
          name="geo.placename"
          content={`${data.cityName}, ${data.state}`}
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative min-h-[400px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: "url('/redebins/images/hero-background.jpg')",
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 text-center px-4 py-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 italic">
            {data.heroTitle}
          </h1>
          <OrderButtons />
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Intro */}
          <h2 className="text-2xl font-bold text-[#E31837] mb-4 italic">
            {'>> '}Dumpster Rental in {data.cityName}
          </h2>
          <p className="text-gray-700 mb-8">{data.introParagraph}</p>

          {/* Why Choose */}
          <h3 className="text-xl font-bold text-[#E31837] mb-4 italic">
            {'>> '}Why Choose RED-E-BINS?
          </h3>
          <p className="text-gray-700 mb-2">
            Choosing a dependable dumpster rental in {data.cityName} matters.
            Here are a few reasons to choose RED-E-BINS:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-8 space-y-1">
            {data.whyChooseReasons.map((reason, index) => (
              <li key={index}>{reason}</li>
            ))}
          </ul>

          {/* Residential */}
          <h3 className="text-xl font-bold text-[#E31837] mb-4 italic">
            {'>> '}Residential Dumpster Rental in {data.cityName}
          </h3>
          <p className="text-gray-700 mb-2">
            Our dumpsters simplify waste disposal for homeowners. Common uses
            include:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            {data.residentialUses.map((use, index) => (
              <li key={index}>{use}</li>
            ))}
          </ul>
          {data.residentialBenefits && (
            <>
              <h4 className="text-lg font-bold text-[#E31837] mb-2 italic">
                {'>> '}Benefits for Homeowners
              </h4>
              <p className="text-gray-700 mb-8">{data.residentialBenefits}</p>
            </>
          )}

          {/* Commercial */}
          <h3 className="text-xl font-bold text-[#E31837] mb-4 italic">
            {'>> '}Commercial Dumpster Rental in {data.cityName}
          </h3>
          <p className="text-gray-700 mb-2">
            Businesses and contractors trust us for their waste disposal needs.
            We provide dumpsters for a variety of projects:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-8 space-y-1">
            {data.commercialUses.map((use, index) => (
              <li key={index}>{use}</li>
            ))}
          </ul>

          {/* Community */}
          <h3 className="text-xl font-bold text-[#E31837] mb-4 italic">
            {'>> '}Serving the {data.cityName} Community
          </h3>
          <p className="text-gray-700 mb-8">{data.communityParagraph}</p>

          {/* Alternative Resources */}
          <h4 className="text-lg font-bold text-[#E31837] mb-4 italic">
            {'>> '}Alternative Disposal Options
          </h4>
          <p className="text-gray-700 mb-2">
            For items unsuitable for dumpsters, consider these alternatives:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-8 space-y-1">
            {data.alternativeResources.map((resource, index) => (
              <li key={index}>
                <a
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#E31837] hover:underline"
                >
                  {resource.name}
                </a>
                : {resource.description}
              </li>
            ))}
          </ul>

          {/* How to Rent */}
          <h3 className="text-xl font-bold text-[#E31837] mb-4 italic">
            {'>> '}How to Rent a Dumpster in {data.cityName}
          </h3>
          <p className="text-gray-700 mb-2">
            Follow these easy steps to rent a dumpster:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
            {data.howToRentSteps.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ul>
          {data.closingParagraph && (
            <p className="text-gray-700 mb-8">{data.closingParagraph}</p>
          )}

          {/* CTA */}
          <div className="text-center bg-gray-100 rounded-lg p-6">
            <h5 className="font-bold text-[#333333] mb-2">Need Assistance?</h5>
            <a
              href="tel:9529312577"
              className="text-2xl font-bold text-[#E31837] hover:underline"
            >
              952-931-2577
            </a>
          </div>
        </div>
      </section>

      {/* Product Cards */}
      <ProductCards />

      {/* Ready When You Are */}
      <ReadyWhenYouAre />

      {/* Service Areas */}
      <ServiceAreasSection />
    </>
  );
};

